import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Overline, Reveal } from "./Reveal";
import Modal from "./Modal";
import { apiFetch, imgSrc, IS_STATIC } from "@/lib/api";
import { getContent, loadRemoteContent, hasLocalOverrides } from "@/lib/staticData";
import { IMAGES } from "@/lib/images";

const FILTERS = [
  { key: "all", label: "All", testid: "gallery-filter-all" },
  { key: "residential", label: "Residential", testid: "gallery-filter-residential" },
  { key: "exterior", label: "Exterior", testid: "gallery-filter-exterior" },
  { key: "commercial", label: "Commercial", testid: "gallery-filter-commercial" },
  { key: "detail", label: "Detail", testid: "gallery-filter-detail" },
];

const ITEMS = [
  { src: IMAGES.lakeside, cat: "residential", title: "Lakeside Residence", meta: "Interior — Full repaint", span: "col-span-2 row-span-2" },
  { src: IMAGES.featureWall, cat: "residential", title: "Studio 9", meta: "Feature wall — Crimson", span: "" },
  { src: IMAGES.kitchen, cat: "detail", title: "Hartman Kitchen", meta: "Cabinet refinish — 2-pac", span: "" },
  { src: IMAGES.birchwood, cat: "exterior", title: "Birchwood Modern", meta: "Exterior — Full system", span: "" },
  { src: IMAGES.commercial, cat: "commercial", title: "Unley Office Precinct", meta: "Commercial — Off-hours", span: "" },
  { src: IMAGES.dining, cat: "residential", title: "Burnside Dining Room", meta: "Interior — Heritage trim", span: "" },
  { src: IMAGES.paving, cat: "exterior", title: "Hills Paving Revival", meta: "Paving paint — Driveway & paths", span: "col-span-2" },
  { src: IMAGES.marble, cat: "detail", title: "Pigment Study N°01", meta: "Lab — Colour calibration", span: "" },
];

export default function GallerySection() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [items, setItems] = useState(IS_STATIC ? getContent().gallery : ITEMS);

  useEffect(() => {
    if (IS_STATIC) {
      loadRemoteContent().then((c) => {
        if (c && !hasLocalOverrides()) setItems(c.gallery);
      });
      return;
    }
    apiFetch("/gallery")
      .then((list) => {
        if (Array.isArray(list) && list.length > 0)
          setItems(
            list.map((g) => ({
              src: imgSrc(g.image),
              cat: g.cat || "residential",
              title: g.title,
              meta: g.meta || "",
              span: g.span || "",
            }))
          );
      })
      .catch(() => {});
  }, []);

  const visible = items.filter((it) => filter === "all" || it.cat === filter);

  return (
    <section id="gallery" className="py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Selected work</Overline>
            <h2 className="mt-4 font-serif text-5xl uppercase leading-[0.95] tracking-tight sm:text-6xl">
              The proof is
              <span className="text-outline block">on the wall.</span>
            </h2>
          </div>
          <div className="flex max-w-xs flex-col gap-6">
            <p className="text-sm leading-relaxed text-muted-foreground">
              A rotating selection of recent interiors, exteriors and
              refinishing projects across greater Adelaide.
            </p>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={f.testid}
                  onClick={() => setFilter(f.key)}
                  className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    filter === f.key
                      ? "border-accent bg-accent text-white"
                      : "border-border text-muted-foreground hover:border-accent hover:text-accent"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="mt-14 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[210px] md:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {visible.map((it) => (
              <motion.button
                layout
                key={it.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                onClick={() => setActive(it)}
                data-testid="gallery-item-card"
                className={`group relative overflow-hidden text-left ${it.span}`}
              >
                <img
                  src={it.src}
                  alt={`${it.title} — ${it.meta}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 p-4 opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <p className="font-serif text-lg uppercase tracking-tight text-white">{it.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">{it.meta}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} label={active?.title || "Project"}>
        {active && (
          <div>
            <img src={active.src} alt={active.title} className="aspect-[16/10] w-full object-cover" />
            <p className="mt-4 font-serif text-2xl uppercase tracking-tight">{active.title}</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{active.meta}</p>
          </div>
        )}
      </Modal>
    </section>
  );
}
